import { Currency, CurrencyUtil, IsoLocale, iZettleMarket } from "@izettle/constants"
import { getLocale } from "@izettle/react"

import { GetTranslationsUrl, TranslationModeConfig } from "./types"

type TenkoTranslations = { [key: string]: string }

export type LocaleOptions = {
  country: iZettleMarket
  language: string
  currency?: Currency
}

export type OnTranslationMissing = (key: string) => string

export type TranslatorParams = TranslationModeConfig & {
  locale?: LocaleOptions
  defaultLocale?: IsoLocale
  allowedLocales?: IsoLocale[]
  translations?: TenkoTranslations
  fallbackDisabled?: boolean
  onTranslationMissing?: OnTranslationMissing
}

export class Translator {
  locale!: IsoLocale

  allowedLocales!: IsoLocale[]

  country!: iZettleMarket

  language!: string

  currency!: Currency

  fallbackDisabled: boolean

  translations: TenkoTranslations

  private readonly onTranslationMissing: OnTranslationMissing = (key) => {
    if (this.isProd) {
      // eslint-disable-next-line no-console
      console.error(`Missing translation for "${key}"`)
    }

    return this.isProd ? "" : key
  }

  private readonly modeConfig: TranslationModeConfig = {}

  private readonly getTranslationsUrl: GetTranslationsUrl = (locale) =>
    `https://localisation.izettle.com/v3/${
      this.modeConfig.projectName ?? "portal"
    }/${locale}.json`

  private readonly isProd = process.env.APP_ENV === "production"

  constructor({
    locale,
    defaultLocale,
    translations,
    onTranslationMissing,
    fallbackDisabled,
    allowedLocales,
    ...modeConfig
  }: TranslatorParams) {
    this.modeConfig = {
      ...this.modeConfig,
      ...modeConfig,
    }

    this.setLocaleVars(
      locale ??
        Translator.optionsFromIsoLocale(
          getLocale(window.navigator.languages, allowedLocales) ??
            defaultLocale ??
            IsoLocale.enGB,
        ),
    )

    this.translations = translations!
    this.onTranslationMissing = onTranslationMissing ?? this.onTranslationMissing
    this.getTranslationsUrl =
      this.modeConfig.getTranslationsUrl ?? this.getTranslationsUrl
    this.fallbackDisabled = fallbackDisabled ?? false
  }

  public static optionsFromIsoLocale<L extends IsoLocale | null | undefined>(
    locale: L,
  ): L extends null | undefined ? LocaleOptions | null | undefined : LocaleOptions {
    if (locale == null) {
      return null as any
    }

    return {
      country: locale.slice(locale.indexOf("-") + 1) as iZettleMarket,
      language: locale.slice(0, locale.indexOf("-")),
    } as LocaleOptions as any
  }

  public static isoLocaleFromOptions(options: LocaleOptions): IsoLocale {
    return `${options.language}-${options.country}` as IsoLocale
  }

  private setLocaleVars(options: LocaleOptions) {
    this.locale = Translator.isoLocaleFromOptions(options)
    this.country = options.country
    this.language = options.language
    this.currency = options.currency ?? CurrencyUtil.fromCountry(this.country)
  }

  translate = (key: string): string => {
    if (!this.translations) {
      return this.isProd ? "" : key
    }

    const translation = this.translations[key.toUpperCase()]
    if (translation) {
      return translation
    }

    return this.onTranslationMissing(key)
  }

  private fetchFile = async (locale: IsoLocale): Promise<TenkoTranslations | null> => {
    const response = await fetch(await this.getTranslationsUrl(locale)).catch(() => null)

    if (
      response == null ||
      !response.ok ||
      !response.headers.get("Content-Type")?.includes("application/json")
    ) {
      // eslint-disable-next-line no-console
      console.error(
        `Failed to fetch translations for ${locale} ${response?.statusText ?? ""}`,
      )

      return null
    }

    try {
      return await response.json()
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error(`Failed to parse translations for ${locale}:\n${e.message}`)
    }

    return null
  }

  /* istanbul ignore next */
  private importFile = async (locale: IsoLocale): Promise<TenkoTranslations | null> => {
    // eslint-disable-next-line import/dynamic-import-chunkname
    const result: Record<string, string> | Error = await import(
      /* webpackChunkName: "[request]" */
      /* webpackMode: "lazy" */
      /* webpackPreload: true */
      /* webpackInclude: /i18n\..*?\.json$/ */
      `@tenko/i18n.${locale}.json`
    ).catch((err) => err)

    if (result instanceof Error) {
      // eslint-disable-next-line no-console
      console.error(`Failed to fetch translations for ${locale} ${result ?? ""}`)

      return null
    }

    return result
  }

  private loadFile = async (locale: IsoLocale): Promise<TenkoTranslations | null> => {
    if (process.env.IZ_APP_TRANSLATION_MODE === "runtime") {
      return this.fetchFile(locale)
    }

    return this.importFile(locale)
  }

  loadTranslations = async (options: { override?: LocaleOptions } = {}) => {
    const locale = options.override
      ? Translator.isoLocaleFromOptions(options.override)
      : this.locale

    let localeBody = await this.loadFile(locale)

    if (localeBody == null && locale !== IsoLocale.enGB && !this.fallbackDisabled) {
      localeBody = await this.loadFile(IsoLocale.enGB)
    }

    if (localeBody == null) {
      throw new Error("Could not load any translations.")
    }

    this.translations = localeBody
    this.setLocaleVars(Translator.optionsFromIsoLocale(locale))
  }
}
